import { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../context/auth-context';
import apiService from '../services/apiService';

/**
 * useProjectPermission
 *
 * Client-side project-scoped permission checks that mirror Maestro's
 * `requirePermissions` semantics. The permission set comes from
 * GET /v1/project/:id/permissions/mine — the server's RESOLVED aggregate
 * (direct membership + every company membership), which is the same set the
 * write endpoints authorize against.
 *
 * Previously this hook read `auth.selectedProject.permissions`, which no
 * endpoint ever populated — every granular-permission user (documents,
 * maintenance, etc. without project.write) rendered a read-only UI even
 * though the API allowed their writes.
 *
 * These checks remain a UX convenience only — the backend enforces the same
 * permissions on every endpoint; a client that mis-evaluates simply gets a
 * 403. Admins short-circuit to true without a fetch.
 *
 * @returns {{ can: (perm: string) => boolean, isAdmin: boolean,
 *             projectPermissions: string[], loaded: boolean }}
 */

// projectId → string[] — resolved sets are stable for a session; pages that
// share the hook shouldn't refetch on every mount.
const permissionCache = new Map();

export default function useProjectPermission() {
  const auth = useContext(AuthContext);
  const role = auth?.user?.role;
  const userPerms = auth?.user?.permissions;
  const projectId = auth?.selectedProject?._id || auth?.selectedProject?.id;
  const token = auth?.token;

  const isAdmin = role === 'admin' || Boolean(userPerms?.includes('admin'));

  const [resolved, setResolved] = useState(() =>
    projectId && permissionCache.has(projectId) ? permissionCache.get(projectId) : null,
  );

  useEffect(() => {
    let cancelled = false;
    if (!projectId || !token || isAdmin) return undefined;
    if (permissionCache.has(projectId)) {
      setResolved(permissionCache.get(projectId));
      return undefined;
    }
    apiService
      .get(`/v1/project/${projectId}/permissions/mine`, token)
      .then((data) => {
        const perms = data.permissions || [];
        permissionCache.set(projectId, perms);
        if (!cancelled) setResolved(perms);
      })
      .catch(() => {
        if (!cancelled) setResolved([]);
      });
    return () => { cancelled = true; };
  }, [projectId, token, isAdmin]);

  return useMemo(() => {
    const projectPermissions = resolved ?? [];
    const can = (perm) =>
      isAdmin || projectPermissions.includes('*') || projectPermissions.includes(perm);
    return { can, isAdmin, projectPermissions, loaded: isAdmin || resolved !== null };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, resolved]);
}
